<template>
    <div>
        <div v-if="step === 'modules'" class="d-flex py-1">
            <v-text-field-filled
                v-model="search"
                label="Rechercher"
                outlined
                density="compact"
                append-inner-icon="$search"
                clearable
                @click:clear="search = ''"
            />
            <v-btn
                v-if="enableWrite && localEnv !== 'production'"
                class="ml-2 mr-0 elevation-0"
                rounded="xl"
                density="compact"
                variant="flat"
                @click="editLearningModule('')"
            >
                Ajouter
            </v-btn>
        </div>
        <v-row>
            <v-col cols="12">
                <v-window v-model="step">
                    <v-window-item value="modules">
                        <div v-if="tutorialsModules.length">
                            <v-list
                                v-if="tutorialsModules.length"
                                v-for="(tutorialsModule, index) in tutorialsModules"
                                :key="index"
                                :value="index"
                                @click.stop="navigateToLearningModule(tutorialsModule.id)"
                            >
                                <v-list-item rounded="xl" lines="two" border>
                                    <!-- Icon Chevron pour déplier/fermer -->
                                    <template #prepend @click.stop="toggleModule(index)">
                                        <v-btn-icon
                                            x-small
                                            :icon="
                                                openModuleIndex === index
                                                    ? '$fa-chevron-down'
                                                    : '$fa-chevron-right'
                                            "
                                            @click.stop="toggleModule(index)"
                                        >
                                        </v-btn-icon>
                                    </template>

                                    <!-- Contenu cliquable pour ouvrir le module -->

                                    <v-list-item-title>{{
                                        tutorialsModule.name
                                    }}</v-list-item-title>
                                    <v-list-item-subtitle>
                                        {{
                                            tutorialsModule.description ||
                                            "Pas de description disponible"
                                        }}
                                    </v-list-item-subtitle>
                                    <template #append>
                                        <v-tooltip location="bottom">
                                            <template v-slot:activator="{ props }">
                                                <v-btn-icon
                                                    v-if="enableWrite && localEnv !== 'production'"
                                                    icon="$oss-edit"
                                                    small
                                                    class="elevation-0"
                                                    color="secondary"
                                                    v-bind="props"
                                                    @click.stop="
                                                        editLearningModule(tutorialsModule.id)
                                                    "
                                                >
                                                </v-btn-icon>
                                            </template>
                                            <span>Editer</span>
                                        </v-tooltip>
                                        <v-tooltip location="bottom">
                                            <template v-slot:activator="{ props }">
                                                <v-progress-circular
                                                    v-if="!isModuleCompleted(tutorialsModule)"
                                                    v-bind="props"
                                                    :model-value="
                                                        calculateModuleProgress(tutorialsModule)
                                                    "
                                                    size="24"
                                                    width="3"
                                                    color="primary"
                                                    rounded
                                                />
                                                <v-icon
                                                    v-if="isModuleCompleted(tutorialsModule)"
                                                    size="24"
                                                    width="3"
                                                    v-bind="props"
                                                >
                                                    $oss-circle-check
                                                </v-icon>
                                            </template>
                                            <span>{{ getModuleStatus(tutorialsModule) }}</span>
                                        </v-tooltip>
                                    </template>
                                </v-list-item>
                                <!-- Liste des tutoriels sous le module avec indentation -->
                                <v-list v-if="openModuleIndex === index" class="ml-4">
                                    <v-list-item
                                        v-for="(
                                            learningObject, i
                                        ) in tutorialsModule.learningObjects"
                                        :key="i"
                                        class="mb-2"
                                        @click.stop="
                                            goToLearningObject(
                                                tutorialsModule.id,
                                                learningObject.id,
                                            )
                                        "
                                        rounded="xl"
                                        border
                                        lines="two"
                                    >
                                        <template #prepend>
                                            <v-icon icon="$fa-photo-video" />
                                        </template>
                                        <v-list-item-title>{{
                                            learningObject.name
                                        }}</v-list-item-title>
                                        <v-list-item-subtitle>
                                            {{
                                                learningObject.description ||
                                                "Pas de description disponible"
                                            }}
                                        </v-list-item-subtitle>
                                        <template #append>
                                            <v-tooltip location="bottom">
                                                <template v-slot:activator="{ props }">
                                                    <v-progress-circular
                                                        color="primary"
                                                        v-if="!isTutorialCompleted(learningObject)"
                                                        v-bind="props"
                                                        :model-value="
                                                            calculateTutorialProgress(
                                                                learningObject,
                                                            )
                                                        "
                                                        size="24"
                                                        width="3"
                                                        rounded
                                                    />
                                                    <v-icon
                                                        v-if="isTutorialCompleted(learningObject)"
                                                        size="24"
                                                        width="3"
                                                        v-bind="props"
                                                        color="primary"
                                                    >
                                                        $oss-circle-check
                                                    </v-icon>
                                                </template>
                                                <span>{{ getTutorialStatus(learningObject) }}</span>
                                            </v-tooltip>
                                        </template>
                                    </v-list-item>
                                </v-list>
                            </v-list>
                        </div>
                        <div v-else>
                            <v-alert
                                density="compact"
                                variant="outlined"
                                color="primary"
                                icon="$fa-exclamation-triangle"
                            >
                                Aucun module d'apprentissage disponible
                            </v-alert>
                        </div>
                    </v-window-item>
                    <v-window-item value="editionModule">
                        <TutorialsModuleEdition
                            v-if="step === 'editionModule'"
                            :learningModuleId="learningModuleId"
                            @refresh="refresh()"
                        />
                    </v-window-item>
                    <v-window-item value="module">
                        <TutorialsModule
                            v-if="learningModuleId && step === 'module'"
                            :learningModuleId="learningModuleId"
                            :learningObjectId="learningObjectId"
                            @upsert-arcade-events="upsertAllArcadeEvents"
                            ref="tutorialsModule"
                        />
                    </v-window-item>
                </v-window>
            </v-col>
        </v-row>
    </div>
</template>

<script lang="ts">
import { Component, Vue, toNative } from "vue-facing-decorator";
import { Apollo } from "../../decorators";
import TUTORIALS_MODULES from "../../graphql/tutorials/TutorialsModules.gql"; // Requête GQL qui récupère les modules par profileType
import getEnv from "../../env";
import UPSERT_ACTIVITY_STEP_COUNT from "../../graphql/usersActivities/UpsertUserActivityStepCount.gql";

import TutorialsModuleEdition from "./TutorialsModuleEdition.vue";
import TutorialsModule from "./TutorialsModule.vue";

const env = getEnv();

@Component({
    components: {
        TutorialsModule,
        TutorialsModuleEdition,
    },
})
export class TutorialsModules extends Vue {
    localEnv = env.environnement;

    search = "";

    step: "modules" | "editionModule" | "module" = "modules";

    learningModuleId = "";

    learningObjectId = null;

    // L'index du module actuellement ouvert
    openModuleIndex: number | null = null;

    get currentUser() {
        return this.$store.state.user;
    }

    get currentUserProfileType() {
        return this.currentUser.profile.type;
    }

    sortedLearningObjects(learningObjects: any[]) {
        return learningObjects.slice().sort((a, b) => a.name.localeCompare(b.name));
    }

    get enableWrite() {
        return this.currentUser.isRoot;
    }

    @Apollo({
        query() {
            return TUTORIALS_MODULES;
        },
        variables() {
            return {
                profileType: this.currentUserProfileType,
            };
        },
        specificResult({ data, loading, networkStatus }: any) {
            if (!loading && data?.tutorialsModules?.tutorialsModules) {
                this.tutorialsModules = [...data.tutorialsModules.tutorialsModules];
            } else {
                this.tutorialsModules = [];
            }
        },
    })
    tutorialsModules = [];

    async upsertAllArcadeEvents(events: any) {
        const upsertedArcadeEvents = [];

        for (const lastArcadeEvent of events) {
            debugger;
            const upsertedArcadeEvent = await this.$apollo.mutate({
                mutation: UPSERT_ACTIVITY_STEP_COUNT,
                variables: {
                    id: lastArcadeEvent.userActivityId || "",
                    userId: this.currentUser.id,
                    learningObjectId: lastArcadeEvent.learningObjectId,
                    tutorialStepCount: 0, // This ensure to not update the tutorial step count
                    userStepCount: lastArcadeEvent.stepCount,
                },
            });

            if (upsertedArcadeEvent?.data?.upsertUserActivityStepCount) {
                upsertedArcadeEvents.push(upsertedArcadeEvent.data.upsertUserActivityStepCount);
            }
        }

        if (upsertedArcadeEvents.length) {
            this.$apollo.queries.tutorialsModules.refetch();
        }
    }

    // Fonction pour basculer l'état du module (ouvert/fermé)
    toggleModule(index: number) {
        if (this.openModuleIndex === index) {
            this.openModuleIndex = null; // Fermer si déjà ouvert
        } else {
            this.openModuleIndex = index; // Ouvrir le module sélectionné
        }
    }

    navigateToLearningModule(moduleId: string) {
        this.step = "module";
        this.learningModuleId = moduleId;
    }

    goToLearningObject(moduleId: string, learningObjectId: number) {
        this.learningModuleId = moduleId;
        this.learningObjectId = learningObjectId;
        this.step = "module";
    }

    editLearningModule(moduleId: string) {
        this.learningModuleId = moduleId;
        this.step = "editionModule";
    }

    refresh() {
        if (this.step !== "modules") {
            this.step = "modules";
        }
        this.$apollo.queries.tutorialsModules.refetch();
    }

    // Méthode pour retourner en arrière
    goBack() {
        // Si on est dans la vue du module, déclencher l'upsert avant de quitter
        if (this.step !== "modules") {
            this.step = "modules";
            this.learningObjectId = 0;
            this.learningModuleId = "";
        }
    }

    calculateModuleProgress(learningModule: any) {
        let globalProgress = 0;

        if (learningModule && learningModule?.learningObjects) {
            for (const learningObject of learningModule.learningObjects) {
                const userActivity = learningObject.UserActivity[0];
                if (userActivity) {
                    if (
                        typeof learningObject.stepCount === "number" &&
                        learningObject.stepCount > 0
                    ) {
                        const progress = (userActivity.stepCount / learningObject.stepCount) * 100;
                        globalProgress += progress;
                    }
                }
            }
            return Math.min(
                Math.max(globalProgress / learningModule.learningObjects.length, 0),
                100,
            );
        }
        return 0;
    }

    isModuleCompleted(learningModule: any) {
        return this.calculateModuleProgress(learningModule) >= 100;
    }

    getModuleStatus(learningModule: any) {
        const progress = this.calculateModuleProgress(learningModule);

        if (progress >= 100) {
            return "Terminé";
        } else if (progress > 0) {
            return "En cours";
        } else {
            return "Pas commencé";
        }
    }

    /** Calculates the progress percentage for a given tutorial.
     * @param tutorial The tutorial object.
     * @returns The progress percentage as a number.
     */
    calculateTutorialProgress(tutorial) {
        if (!tutorial.UserActivity || tutorial.UserActivity.length === 0) {
            return 0;
        }

        // Utiliser le stepCount du tutoriel et le userStepCount de l'utilisateur pour le calcul de progression
        const userActivity = tutorial.UserActivity[0];
        if (userActivity && typeof tutorial.stepCount === "number" && tutorial.stepCount > 0) {
            const progress = (userActivity.stepCount / tutorial.stepCount) * 100;
            return Math.min(Math.max(progress, 0), 100);
        } else {
            return 0;
        }
    }

    isTutorialCompleted(tutorial) {
        if (tutorial.progressPercentage) {
            return parseFloat(tutorial.progressPercentage.replace("%", "")) >= 100;
        }

        const progress = this.calculateTutorialProgress(tutorial);
        return progress >= 100;
    }

    getTutorialStatus(tutorial) {
        const progress = this.calculateTutorialProgress(tutorial);
        if (progress >= 100) {
            return "Terminé";
        } else if (progress > 0) {
            return "En cours";
        } else {
            return "Pas commencé";
        }
    }
}

export default toNative(TutorialsModules);
</script>

<style scoped></style>
